/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { handleApiError } from "../utils/errorHandler";
import { ArrowLeft, Plus, Copy, Trash2, Save } from "lucide-react";
import Layout from "../components/Layout";
import MonthlyArtworkSelector from "../components/MonthlyArtworkSelector";
import {
  fetchCustomers,
  fetchMagazines,
  fetchContentSizes,
} from "../store/slices/bookingSlice";
import api, { customersAPI, bookingsAPI } from "../utils/api";
import { toast } from "react-toastify";
import { getLatestSelectedMonth } from "../utils/monthUtils";

const magazineEntrySchema = yup.object({
  magazine: yup.string().required("Magazine is required"),
  contentSize: yup.string().required("Rate Card is required"),
  contentType: yup.string().required("Type is required"),
  listPrice: yup.number().required("List price is required").min(0),
  discountPercentage: yup
    .number()
    .transform((value, originalValue) => {
      return originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
        ? 0
        : value;
    })
    .min(0)
    .max(100)
    .default(0),
  discountValue: yup
    .number()
    .transform((value, originalValue) => {
      return originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
        ? 0
        : value;
    })
    .min(0)
    .default(0),
  additionalCharge: yup
    .number()
    .transform((value, originalValue) => {
      return originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
        ? 0
        : value;
    })
    .min(0)
    .default(0),
  monthlyArtwork: yup
    .array()
    .of(
      yup.object({
        month: yup.string().required(),
        isSelected: yup.boolean().default(false),
        artworkNote: yup.string().default(""),
        filename: yup.string().default(""),
        isClosed: yup.boolean().default(false),
      }),
    )
    .min(1, "At least one issue must be selected")
    .default([]),
  // Keep for backward compatibility
  finishIssue: yup
    .string()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
        ? null
        : value;
    }),
  isOngoing: yup.boolean().default(false),
  isLocked: yup.boolean().default(false), // Locked when first selected issue is closed
});

const bookingSchema = yup.object({
  customer: yup.string().required("Customer is required"),
  magazineEntries: yup
    .array()
    .of(magazineEntrySchema)
    .min(1, "At least one magazine entry is required"),
  notes: yup.string(),
});

// The publication select column is narrow, so we don't show the full name.
// Take words one at a time until the result exceeds 10 characters (or we run out).
// "The Hampshire Magazine" → "The Hampshire"; "A" → "A"; "Pet Magazine" → "Pet Magazine".
const shortMagazineLabel = (name) => {
  if (!name) return "";
  const words = name.split(" ");
  let result = "";
  for (const word of words) {
    result = result ? `${result} ${word}` : word;
    if (result.length >= 10) break;
  }
  return result;
};

const NewBooking = () => {
  // Edit mode is identified by the customerId URL param (/bookings/customer/:customerId).
  // Create mode has no param (/bookings/new). After the flatten refactor each customer's
  // "booking" is actually N flat Booking documents, one per row in the form.
  const { customerId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customers, magazines, contentSizes } = useSelector(
    (state) => state.booking,
  );

  const isEditMode = Boolean(customerId);

  const [selectedCustomer, setSelectedCustomer] = useState("");
  // In edit mode this holds the full set of flat Booking docs for the customer (pre-edit state).
  // Used at submit time to compute the create/update/deleteIds diff.
  const [existingBookings, setExistingBookings] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [availableIssues, setAvailableIssues] = useState({});
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
  const [hasUnsavedArtworkChanges, setHasUnsavedArtworkChanges] =
    useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showClosedBookings, setShowClosedBookings] = useState(false);
  const customerDropdownRef = useRef(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(bookingSchema),
    defaultValues: {
      customer: "",
      magazineEntries: [
        {
          _bookingId: undefined,
          magazine: "",
          contentSize: "",
          contentType: "Advert",
          listPrice: 0,
          discountPercentage: 0,
          discountValue: 0,
          additionalCharge: 0,
          monthlyArtwork: [],
          finishIssue: "",
          isOngoing: false,
          isLocked: false,
        },
      ],
      notes: "",
    },
  });

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: "magazineEntries",
  });

  const watchedCustomer = watch("customer");
  const watchedEntries = watch("magazineEntries");

  useEffect(() => {
    loadInitialData();
  }, [dispatch]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        fetchAllCustomers(), // Fetch all customers without pagination
        dispatch(fetchMagazines(true)), // Include archived magazines for booking form
        dispatch(fetchContentSizes(true)), // Include archived content sizes for booking form
        fetchContentTypes(),
      ]);
    } catch (error) {
      handleApiError(error, "Failed to load initial data");
    }
  };

  const fetchAllCustomers = async () => {
    try {
      const response = await customersAPI.getAll({ limit: 50000, page: 1 });
      dispatch({
        type: "booking/fetchCustomers/fulfilled",
        payload: response.data,
      });
    } catch (error) {
      dispatch(fetchCustomers({ limit: 50000, page: 1 }));
    }
  };

  // Edit mode: load all flat Bookings for the customer and populate the form rows.
  useEffect(() => {
    if (customerId) {
      loadCustomerBookings(customerId);
    }
  }, [customerId]);

  // Create mode: when the user selects a customer who already has bookings,
  // transition to the edit URL so we load that customer's existing rows.
  useEffect(() => {
    if (watchedCustomer && !customerId && !hasUserMadeChanges) {
      setSelectedCustomer(watchedCustomer);
      fetchCustomerBookings(watchedCustomer);
    }
  }, [watchedCustomer, customerId, hasUserMadeChanges]);

  // Filter customers based on search input
  useEffect(() => {
    if (customerSearch.trim() === "") {
      setFilteredCustomers(customers.slice(0, 50)); // Show first 50 when no search
    } else {
      const filtered = customers
        .filter((customer) =>
          customer.name.toLowerCase().includes(customerSearch.toLowerCase()),
        )
        .slice(0, 50); // Limit to 50 results
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customers]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Warn user about unsaved changes when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedArtworkChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved artwork changes. Are you sure you want to leave?";
        return "You have unsaved artwork changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedArtworkChanges]);

  // Auto-fill list price when both magazine and content size are selected
  const handlePriceAutoFill = (index, magazineId, contentSizeId) => {
    if (magazineId && contentSizeId) {
      // Find the content size in the Redux store
      const contentSize = contentSizes.find((cs) => cs._id === contentSizeId);

      if (contentSize && contentSize.pricing) {
        // Find the pricing for this specific magazine
        const pricing = contentSize.pricing.find(
          (p) => p.magazine._id === magazineId || p.magazine === magazineId,
        );

        if (pricing && pricing.price !== undefined) {
          setValue(`magazineEntries.${index}.listPrice`, pricing.price);
          setHasUserMadeChanges(true);
        } else {
          // No price configured for this combination, set to 0
          setValue(`magazineEntries.${index}.listPrice`, 0);
        }
      } else {
        // Content size not found or has no pricing, set to 0
        setValue(`magazineEntries.${index}.listPrice`, 0);
      }
    }
  };

  const loadAvailableIssues = async (magazineIds) => {
    const issueMap = {
      ...availableIssues,
    };

    for (const magazineId of magazineIds) {
      try {
        // Request all issues including closed ones for booking forms
        const response = await api.get(
          `/magazines/${magazineId}/published-issues?includeClosed=true`,
        );
        issueMap[magazineId] = response.data.issues || [];

        if (!response.data.issues || response.data.issues.length === 0) {
        }
      } catch (error) {
        const magazine = magazines.find((m) => m._id === magazineId);
        if (magazine && magazine.schedule && magazine.schedule.issues) {
          issueMap[magazineId] = magazine.schedule.issues;
        } else {
          issueMap[magazineId] = [];
        }
      }
    }

    const currentMagazineIds = new Set(magazineIds);
    Object.keys(issueMap).forEach((id) => {
      if (!currentMagazineIds.has(id)) {
        delete issueMap[id];
      }
    });

    setAvailableIssues(issueMap);
    // Return the issueMap so it can be used immediately without waiting for state update
    return issueMap;
  };

  useEffect(() => {
    // Load available issues for selected magazines
    const magazineIds = watchedEntries
      .map((entry) => entry.magazine)
      .filter(Boolean);
    if (magazineIds.length > 0 && magazines.length > 0) {
      loadAvailableIssues(magazineIds);
    }
  }, [watchedEntries, magazines]);

  const fetchContentTypes = async () => {
    try {
      const response = await api.get("/content-types");
      setContentTypes(response.data);
    } catch (error) {
      setContentTypes([
        {
          _id: "default-1",
          name: "Advert",
        },
        {
          _id: "default-2",
          name: "Article",
        },
        {
          _id: "default-3",
          name: "Puzzle",
        },
        {
          _id: "default-4",
          name: "Advertorial",
        },
        {
          _id: "default-5",
          name: "Front Cover",
        },
        {
          _id: "default-6",
          name: "In-house",
        },
      ]);
    }
  };

  // Load all flat bookings for one customer and populate the form with one row per booking.
  const loadCustomerBookings = async (customerIdToLoad) => {
    try {
      const response = await bookingsAPI.forCustomer(customerIdToLoad);
      const bookings = response.data || [];

      setExistingBookings(bookings);

      if (bookings.length === 0) {
        // Customer has no existing bookings — start with one empty row but stay in edit URL
        const customerName =
          customers.find((c) => c._id === customerIdToLoad)?.name || "";
        setSelectedCustomer(customerIdToLoad);
        setCustomerSearch(customerName);
        reset({
          customer: customerIdToLoad,
          magazineEntries: [emptyRow()],
          notes: "",
        });
        return;
      }

      populateFormWithBookings(bookings, customerIdToLoad);

      // Load issues for all magazines in the form, then merge isClosed flags + lock status
      const magazineIds = bookings
        .map((b) =>
          b.magazine && typeof b.magazine === "object"
            ? b.magazine._id
            : b.magazine,
        )
        .filter(Boolean);

      if (magazineIds.length > 0) {
        const issuesMap = await loadAvailableIssues(magazineIds);
        await new Promise((resolve) => setTimeout(resolve, 100));

        bookings.forEach((booking, index) => {
          const magazineId =
            booking.magazine && typeof booking.magazine === "object"
              ? booking.magazine._id
              : booking.magazine;
          if (
            !magazineId ||
            !booking.monthlyArtwork ||
            booking.monthlyArtwork.length === 0
          ) {
            return;
          }

          const magazineIssues = issuesMap[magazineId] || [];
          const issueMap = new Map(
            magazineIssues.map((issue) => [issue.name, issue]),
          );

          const updatedMonthlyArtwork = booking.monthlyArtwork.map(
            (artwork) => {
              const issueData = issueMap.get(artwork.month);
              if (issueData) {
                const isClosed =
                  issueData.isClosed !== undefined
                    ? issueData.isClosed
                    : issueData.status === "Closed" ||
                      (issueData.closeDate &&
                        new Date(issueData.closeDate) < new Date());
                return { ...artwork, isClosed };
              }
              return {
                ...artwork,
                isClosed:
                  artwork.isClosed !== undefined ? artwork.isClosed : false,
              };
            },
          );

          setValue(
            `magazineEntries.${index}.monthlyArtwork`,
            updatedMonthlyArtwork,
          );

          const hasClosedSelected = updatedMonthlyArtwork.some(
            (a) => a.isSelected && a.isClosed,
          );
          if (hasClosedSelected) {
            setValue(`magazineEntries.${index}.isLocked`, true);
          }
        });
      }

      setSelectedCustomer(customerIdToLoad);
    } catch (error) {
      handleApiError(error, "Failed to load customer bookings");
      navigate("/bookings");
    }
  };

  const emptyRow = () => ({
    magazine: "",
    contentSize: "",
    contentType: "Advert",
    listPrice: 0,
    discountPercentage: 0,
    discountValue: 0,
    additionalCharge: 0,
    monthlyArtwork: [],
    finishIssue: "",
    isOngoing: false,
    isLocked: false,
    _bookingId: undefined,
  });

  // Create-mode customer selection: if the customer has existing bookings, redirect
  // to the edit URL so the form loads them. Otherwise keep the empty form for a brand-new booking.
  const fetchCustomerBookings = async (pickedCustomerId) => {
    try {
      const response = await bookingsAPI.forCustomer(pickedCustomerId);
      const customerBookings = response.data || [];

      if (customerBookings.length > 0) {
        navigate(`/bookings/customer/${pickedCustomerId}`);
        return;
      }

      setExistingBookings([]);
      reset({
        customer: pickedCustomerId,
        magazineEntries: [emptyRow()],
        notes: "",
      });
      setHasUnsavedArtworkChanges(false);
      setAvailableIssues({});
    } catch (error) {
      setExistingBookings([]);
      reset({
        customer: pickedCustomerId,
        magazineEntries: [emptyRow()],
        notes: "",
      });
      setHasUnsavedArtworkChanges(false);
      setAvailableIssues({});
    }
  };

  // Populate the form with N rows, one per flat Booking. The customer's notes are pulled
  // from the first booking — notes are now per-booking, but the UI exposes a single field.
  const populateFormWithBookings = (bookings, idForCustomer) => {
    const rows = bookings.map((b) => ({
      _bookingId: b._id,
      magazine:
        b.magazine && typeof b.magazine === "object"
          ? b.magazine._id
          : b.magazine || "",
      contentSize:
        b.contentSize && typeof b.contentSize === "object"
          ? b.contentSize._id
          : b.contentSize || "",
      contentType: b.contentType || "Advert",
      listPrice: Number(b.listPrice) || 0,
      discountPercentage: Number(b.discountPercentage) || 0,
      discountValue: Number(b.discountValue) || 0,
      additionalCharge: Number(b.additionalCharge) || 0,
      monthlyArtwork: b.monthlyArtwork || [],
      startIssue: b.startIssue || "",
      finishIssue: b.finishIssue || "",
      isOngoing: !!b.isOngoing,
      isLocked: false,
    }));

    const firstCustomer = bookings[0]?.customer;
    const customerName =
      firstCustomer && typeof firstCustomer === "object"
        ? firstCustomer.name
        : customers.find((c) => c._id === idForCustomer)?.name || "";

    reset({
      customer: idForCustomer,
      magazineEntries: rows.length > 0 ? rows : [emptyRow()],
      notes: bookings[0]?.notes || "",
    });

    setCustomerSearch(customerName);
    setHasUnsavedArtworkChanges(false);

    const magazineIds = rows.map((r) => r.magazine).filter(Boolean);
    if (magazineIds.length > 0 && magazines.length > 0) {
      loadAvailableIssues(magazineIds);
    }
  };

  const addRow = () => {
    prepend(emptyRow());
    setHasUserMadeChanges(true);
  };

  const handleMagazineIssuesLoad = (index, magazineId, issuesMap = null) => {
    const magazine = magazines.find((m) => m._id === magazineId);
    if (!magazine) {
      return;
    }

    const magazineIssues = issuesMap
      ? issuesMap[magazineId] || []
      : availableIssues[magazineId] || [];

    if (magazineIssues.length > 0) {
      const monthlyArtwork = magazineIssues.map((issue) => ({
        month: issue.name,
        isSelected: false,
        artworkNote: "",
        filename: "",
        isClosed:
          issue.isClosed !== undefined
            ? issue.isClosed
            : issue.status === "Closed" ||
              (issue.closeDate && new Date(issue.closeDate) < new Date()),
      }));

      setValue(`magazineEntries.${index}.monthlyArtwork`, monthlyArtwork);

      checkAndUpdateLockStatus(index, monthlyArtwork);
    }
  };

  const checkAndUpdateLockStatus = (index, monthlyArtwork) => {
    const selectedIssues = monthlyArtwork.filter((m) => m.isSelected);
    if (selectedIssues.length > 0) {
      const firstSelectedIssue = selectedIssues[0];
      const isLocked = firstSelectedIssue.isClosed || false;
      setValue(`magazineEntries.${index}.isLocked`, isLocked);
    } else {
      setValue(`magazineEntries.${index}.isLocked`, false);
    }
  };

  // isOngoing is optional: the modal now owns the Ongoing checkbox and passes the
  // new value back here. When omitted (other callers), fall back to the row's
  // current value so existing behaviour is unchanged.
  const handleMonthlyArtworkChange = (index, monthlyArtwork, isOngoing) => {
    setValue(`magazineEntries.${index}.monthlyArtwork`, monthlyArtwork);

    const entry = watchedEntries[index];
    const newIsOngoing =
      isOngoing !== undefined ? isOngoing : entry?.isOngoing || false;
    setValue(`magazineEntries.${index}.isOngoing`, newIsOngoing);

    checkAndUpdateLockStatus(index, monthlyArtwork);

    if (!newIsOngoing) {
      const latestMonth = getLatestSelectedMonth(monthlyArtwork);
      if (latestMonth) {
        setValue(`magazineEntries.${index}.finishIssue`, latestMonth);
      } else {
        setValue(`magazineEntries.${index}.finishIssue`, "");
      }
    } else {
      // Ongoing bookings are open-ended - no finish issue.
      setValue(`magazineEntries.${index}.finishIssue`, null);
    }

    setHasUserMadeChanges(true);
    setHasUnsavedArtworkChanges(true);
  };

  const copyRow = (index) => {
    const rowToCopy = watchedEntries[index];
    const copiedMonthlyArtwork = (rowToCopy.monthlyArtwork || []).map(
      (artwork) => ({
        ...artwork,
      }),
    );
    prepend({
      ...rowToCopy,
      _bookingId: undefined, // A copy is a NEW row, not an update of the source booking
      monthlyArtwork: copiedMonthlyArtwork,
      finishIssue: "",
      isOngoing: false,
      isLocked: false,
    });
    setHasUserMadeChanges(true);
  };

  const deleteRow = (index) => {
    remove(index);
    setHasUserMadeChanges(true); // Mark that user has made changes
  };

  // Check if entry has any selected closed issues
  const hasSelectedClosedIssues = (entry) => {
    if (!entry.monthlyArtwork || entry.monthlyArtwork.length === 0) {
      return false;
    }
    return entry.monthlyArtwork.some(
      (artwork) => artwork.isSelected && artwork.isClosed,
    );
  };

  // A booking is "closed" (finished) only when it has at least one selected issue
  // AND every selected issue is closed. Distinct from hasSelectedClosedIssues (which
  // uses .some() to drive the row lock for partially-closed bookings).
  const isBookingClosed = (entry) => {
    const selected = (entry.monthlyArtwork || []).filter((a) => a.isSelected);
    if (selected.length === 0) return false; // empty/new rows stay visible
    return selected.every((a) => a.isClosed);
  };

  const closedBookingCount = watchedEntries.filter(isBookingClosed).length;

  const calculateRowTotal = (entry) => {
    const listPrice = Number(entry.listPrice) || 0;
    const discountPercentage = Number(entry.discountPercentage) || 0;
    const discountValue = Number(entry.discountValue) || 0;
    const additionalCharge = Number(entry.additionalCharge) || 0;

    // Apply percentage discount first, then absolute discount, then add additional charges
    const afterPercentageDiscount = listPrice * (1 - discountPercentage / 100);
    const afterDiscount = Math.max(0, afterPercentageDiscount - discountValue);
    const finalPrice = afterDiscount + additionalCharge;

    return finalPrice;
  };

  const calculateTotalValue = () => {
    const entriesTotal = watchedEntries.reduce((sum, entry) => {
      return sum + calculateRowTotal(entry);
    }, 0);
    return entriesTotal;
  };

  // Strip UI-only fields and add totalPrice for one form row.
  const rowToBookingPayload = (entry) => {
    const { _bookingId, isLocked, ...rest } = entry;
    return {
      ...rest,
      totalPrice: calculateRowTotal(entry),
    };
  };

  const onSubmit = async (data) => {
    try {
      if (isEditMode) {
        // Build diff against the bookings we loaded at mount.
        const submittedRows = data.magazineEntries || [];
        const existingIds = new Set(existingBookings.map((b) => String(b._id)));
        const submittedIds = new Set(
          submittedRows
            .filter((r) => r._bookingId)
            .map((r) => String(r._bookingId)),
        );

        const create = submittedRows
          .filter((r) => !r._bookingId)
          .map(rowToBookingPayload);

        const update = submittedRows
          .filter((r) => r._bookingId && existingIds.has(String(r._bookingId)))
          .map((r) => ({ _id: r._bookingId, ...rowToBookingPayload(r) }));

        const deleteIds = existingBookings
          .filter((b) => !submittedIds.has(String(b._id)))
          .map((b) => String(b._id));

        await bookingsAPI.sync({
          customer: data.customer,
          notes: data.notes,
          create,
          update,
          deleteIds,
        });

        toast.success("Bookings saved");
      } else {
        // Create-mode batch insert.
        const bookings = (data.magazineEntries || []).map(rowToBookingPayload);
        await bookingsAPI.create({
          customer: data.customer,
          notes: data.notes,
          bookings,
        });
        toast.success("Booking created successfully");
      }

      setHasUserMadeChanges(false);
      setHasUnsavedArtworkChanges(false);
      navigate("/bookings");
    } catch (error) {
      handleApiError(error, "Failed to save booking");
    }
  };

  const formatCurrency = (value) => `£${(value || 0).toFixed(2)}`;

  const handleCustomerSelect = (customer) => {
    setValue("customer", customer._id);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    setSelectedCustomer(customer._id);
    fetchCustomerBookings(customer._id);

    // Blur the input field to remove focus and prevent dropdown from reopening
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);

    if (
      selectedCustomer &&
      value !== customers.find((c) => c._id === selectedCustomer)?.name
    ) {
      setValue("customer", "");
      setSelectedCustomer("");
      setExistingBookings([]);
    }

    if (
      !selectedCustomer ||
      value !== customers.find((c) => c._id === selectedCustomer)?.name
    ) {
      setShowCustomerDropdown(true);
    }

    if (value === "") {
      setValue("customer", "");
      setSelectedCustomer("");
      setExistingBookings([]);
    }
  };

  const toggleRowExpansion = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  return (
    <Layout>
      <div className="py-8 min-h-screen bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {" "}
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate("/bookings")}
              className="flex items-center mb-4 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="mr-1 w-4 h-4" />
              Back to Bookings
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              {" "}
              {isEditMode ? "Edit Booking" : "Add New Booking"}{" "}
            </h1>
            <p className="mt-2 text-gray-600">
              {" "}
              {isEditMode
                ? "Modify existing booking entries"
                : "Create a new booking for a customer"}{" "}
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {" "}
            {/* Customer Selection */}
            <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">Customer</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {" "}
                  {isEditMode
                    ? "Customer for this booking"
                    : "Select the customer for this booking"}{" "}
                </p>
              </div>
              {isEditMode ? (
                /* Read-only customer display for edit mode */ <div className="mt-1">
                  <div className="block px-3 py-2 w-full text-gray-900 bg-gray-50 rounded-md border border-gray-300 shadow-sm sm:text-sm">
                    {" "}
                    {customerSearch || "Loading customer..."}{" "}
                  </div>
                  {/* Hidden input for form validation */}
                  <input type="hidden" {...register("customer")} />
                </div>
              ) : (
                /* Searchable customer dropdown for create mode */ <div
                  className="relative"
                  ref={customerDropdownRef}
                >
                  <div className="relative">
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={handleCustomerSearchChange}
                      onFocus={() => {
                        // Only show dropdown if no customer is selected
                        if (!selectedCustomer) {
                          setShowCustomerDropdown(true);
                        }
                      }}
                      placeholder="Search for a customer..."
                      className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        selectedCustomer
                          ? "pr-10 bg-green-50 border-green-300"
                          : ""}`}
                    />{" "}
                    {/* Clear button when customer is selected */}
                    {selectedCustomer && (
                      <button
                        type="button"
                        onClick={() => {
                          setValue("customer", "");
                          setSelectedCustomer("");
                          setCustomerSearch("");
                          setExistingBookings([]);
                          setShowCustomerDropdown(false);
                        }}
                        className="flex absolute inset-y-0 right-0 items-center pr-3 mt-1"
                      >
                        <svg
                          className="w-4 h-4 text-gray-400 hover:text-gray-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}{" "}
                  </div>
                  {/* Hidden input for form validation */}
                  <input type="hidden" {...register("customer")} />{" "}
                  {/* Dropdown with filtered customers */}
                  {showCustomerDropdown &&
                    filteredCustomers.length > 0 &&
                    !selectedCustomer && (
                      <div className="overflow-auto absolute z-10 py-1 mt-1 w-full max-h-60 text-base bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg focus:outline-none sm:text-sm">
                        {" "}
                        {filteredCustomers.map((customer) => (
                          <div
                            key={customer._id}
                            onClick={() => handleCustomerSelect(customer)}
                            className="relative py-2 pr-9 pl-3 cursor-pointer select-none hover:bg-blue-50 hover:text-blue-900"
                          >
                            <span className="block truncate">
                              {" "}
                              {customer.name}
                            </span>
                          </div>
                        ))}
                        {customers.length > filteredCustomers.length && (
                          <div className="py-2 pr-9 pl-3 text-sm text-gray-500">
                            {" "}
                            {customerSearch
                              ? `Showing ${filteredCustomers.length} results`
                              : "Showing first 50 customers - type to search for more"}{" "}
                          </div>
                        )}{" "}
                      </div>
                    )}
                  {/* Show message when no customers found */}
                  {showCustomerDropdown &&
                    customerSearch &&
                    filteredCustomers.length === 0 &&
                    !selectedCustomer && (
                      <div className="absolute z-10 py-1 mt-1 w-full text-base bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg">
                        <div className="py-2 pr-9 pl-3 text-sm text-gray-500">
                          No customers found matching "{customerSearch}"
                        </div>
                      </div>
                    )}{" "}
                </div>
              )}
              {errors.customer && (
                <p className="mt-1 text-sm text-red-600">
                  {" "}
                  {errors.customer.message}
                </p>
              )}{" "}
            </div>
            {selectedCustomer && (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Booking Details
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Configure publication bookings for this customer
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addRow}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Plus className="mr-1 w-4 h-4" />
                      Add Row
                    </button>
                  </div>

                  {/* Locked Booking Warning */}
                  {watchedEntries.some(
                    (entry) => entry.isLocked && !isBookingClosed(entry),
                  ) && (
                    <div className="p-4 mb-4 bg-amber-50 rounded-md border border-amber-200">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg
                            className="w-5 h-5 text-amber-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-amber-800">
                            Booking Locked
                          </h3>
                          <div className="mt-1 text-sm text-amber-700">
                            One or more booking entries have closed issues.
                            Booking details (size, discount, etc.) cannot be
                            changed for bookings with closed issues. You can
                            still select/deselect open issues and edit artwork
                            details.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-300 table-auto">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{ width: "150px" }}
                          >
                            Publication
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{ width: "120px" }}
                          >
                            Size
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{ width: "120px" }}
                          >
                            Type
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{
                              minWidth: "100px",
                              width: "120px",
                            }}
                          >
                            List
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{
                              minWidth: "90px",
                              width: "100px",
                            }}
                          >
                            Discount %
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{
                              minWidth: "90px",
                              width: "100px",
                            }}
                          >
                            Discount £
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{
                              minWidth: "90px",
                              width: "100px",
                            }}
                          >
                            Uplift
                            <br />£
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{
                              minWidth: "90px",
                              width: "100px",
                            }}
                          >
                            Total
                          </th>
                          <th
                            className="px-3 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{ width: "170px" }}
                          >
                            Issues/Artwork
                          </th>
                          <th
                            className="px-2 py-3 text-xs font-medium tracking-wide text-left text-gray-500"
                            style={{ width: "50px" }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {fields.map((field, index) => {
                          const entry = watchedEntries[index] || {};
                          // Hide closed bookings by default, but keep the row at its
                          // true index so react-hook-form field paths stay correct.
                          if (isBookingClosed(entry) && !showClosedBookings) {
                            return null;
                          }
                          return (
                            <React.Fragment key={field.id}>
                              <tr
                                className={entry.isLocked ? "bg-amber-50" : ""}
                              >
                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{ width: "150px" }}
                                >
                                  <div className="flex gap-1 items-center">
                                    <select
                                      {...register(
                                        `magazineEntries.${index}.magazine`,
                                      )}
                                      onChange={(e) => {
                                        const magazineId = e.target.value;
                                        setValue(
                                          `magazineEntries.${index}.magazine`,
                                          magazineId,
                                        );

                                        setValue(
                                          `magazineEntries.${index}.monthlyArtwork`,
                                          [],
                                        );
                                        setValue(
                                          `magazineEntries.${index}.finishIssue`,
                                          "",
                                        );
                                        setValue(
                                          `magazineEntries.${index}.isOngoing`,
                                          false,
                                        );
                                        setValue(
                                          `magazineEntries.${index}.isLocked`,
                                          false,
                                        );

                                        const currentEntry =
                                          watchedEntries[index];
                                        const contentSizeId =
                                          currentEntry?.contentSize;

                                        if (magazineId && contentSizeId) {
                                          handlePriceAutoFill(
                                            index,
                                            magazineId,
                                            contentSizeId,
                                          );
                                        } else {
                                          setValue(
                                            `magazineEntries.${index}.listPrice`,
                                            0,
                                          );
                                        }

                                        setHasUserMadeChanges(true);

                                        const currentEntries =
                                          watch("magazineEntries");
                                        const allMagazineIds = currentEntries
                                          .map((entry, i) =>
                                            i === index
                                              ? magazineId
                                              : entry.magazine,
                                          )
                                          .filter(Boolean);

                                        if (
                                          magazineId &&
                                          allMagazineIds.length > 0
                                        ) {
                                          loadAvailableIssues(allMagazineIds)
                                            .then((issuesMap) => {
                                              // Use the returned issuesMap directly instead of waiting for state update
                                              handleMagazineIssuesLoad(
                                                index,
                                                magazineId,
                                                issuesMap,
                                              );
                                            })
                                            .catch((error) => {
                                              handleMagazineIssuesLoad(
                                                index,
                                                magazineId,
                                              );
                                            });
                                        }
                                      }}
                                      disabled={entry.isLocked}
                                      className={`block w-full min-w-28 border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                        entry.isLocked
                                          ? "bg-gray-100 cursor-not-allowed"
                                          : errors.magazineEntries?.[index]
                                                ?.magazine
                                            ? "border-red-500"
                                            : "border-gray-300"
                                      }`}
                                    >
                                      <option value="">-</option>
                                      {[...magazines]
                                        .sort((a, b) => {
                                          const dateA = new Date(
                                            a.updatedAt || a.createdAt,
                                          );
                                          const dateB = new Date(
                                            b.updatedAt || b.createdAt,
                                          );
                                          return dateB - dateA; // Most recent first
                                        })
                                        .map((magazine) => (
                                          <option
                                            key={magazine._id}
                                            value={magazine._id}
                                            title={magazine.name}
                                          >
                                            {" "}
                                            {shortMagazineLabel(
                                              magazine.name,
                                            )}{" "}
                                          </option>
                                        ))}
                                    </select>
                                    {entry.isLocked && (
                                      <svg
                                        className="flex-shrink-0 w-4 h-4 text-amber-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        title="Booking locked - first issue is closed"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        />
                                      </svg>
                                    )}{" "}
                                  </div>
                                </td>

                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{ width: "80px" }}
                                >
                                  <select
                                    {...register(
                                      `magazineEntries.${index}.contentSize`,
                                    )}
                                    onChange={(e) => {
                                      const contentSizeId = e.target.value;
                                      setValue(
                                        `magazineEntries.${index}.contentSize`,
                                        contentSizeId,
                                      );

                                      // Get the current magazine for this row
                                      const currentEntry =
                                        watchedEntries[index];
                                      const magazineId = currentEntry?.magazine;

                                      // Auto-fill price if both magazine and content size are selected
                                      if (magazineId && contentSizeId) {
                                        handlePriceAutoFill(
                                          index,
                                          magazineId,
                                          contentSizeId,
                                        );
                                      }

                                      setHasUserMadeChanges(true);
                                    }}
                                    disabled={entry.isLocked}
                                    className={`block w-full min-w-14 border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                      entry.isLocked
                                        ? "bg-gray-100 cursor-not-allowed"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <option value="">-</option>
                                    {[...contentSizes]
                                      .sort((a, b) => {
                                        const dateA = new Date(
                                          a.updatedAt || a.createdAt,
                                        );
                                        const dateB = new Date(
                                          b.updatedAt || b.createdAt,
                                        );
                                        return dateB - dateA; // Most recent first
                                      })
                                      .map((size) => (
                                        <option key={size._id} value={size._id}>
                                          {" "}
                                          {size.adSize?.name || "N/A"}{" "}
                                        </option>
                                      ))}
                                  </select>
                                </td>

                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{ width: "120px" }}
                                >
                                  <select
                                    {...register(
                                      `magazineEntries.${index}.contentType`,
                                    )}
                                    onChange={(e) => {
                                      setValue(
                                        `magazineEntries.${index}.contentType`,
                                        e.target.value,
                                      );
                                      setHasUserMadeChanges(true);
                                    }}
                                    disabled={entry.isLocked}
                                    className={`block w-full min-w-28 border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                      entry.isLocked
                                        ? "bg-gray-100 cursor-not-allowed"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    {" "}
                                    {[...contentTypes]
                                      .sort((a, b) => {
                                        const dateA = new Date(
                                          a.updatedAt || a.createdAt,
                                        );
                                        const dateB = new Date(
                                          b.updatedAt || b.createdAt,
                                        );
                                        return dateB - dateA; // Most recent first
                                      })
                                      .map((type) => (
                                        <option
                                          key={type._id}
                                          value={type.name}
                                        >
                                          {" "}
                                          {type.name}{" "}
                                        </option>
                                      ))}
                                  </select>
                                </td>

                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{
                                    minWidth: "100px",
                                    width: "120px",
                                  }}
                                >
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register(
                                      `magazineEntries.${index}.listPrice`,
                                    )}
                                    onChange={(e) => {
                                      setValue(
                                        `magazineEntries.${index}.listPrice`,
                                        e.target.value,
                                      );
                                      setHasUserMadeChanges(true);
                                    }}
                                    disabled={true}
                                    className="block px-2 py-1 w-full text-sm bg-gray-100 rounded-md border border-gray-300 shadow-sm cursor-not-allowed focus:outline-none"
                                  />
                                </td>

                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{
                                    minWidth: "90px",
                                    width: "100px",
                                  }}
                                >
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    {...register(
                                      `magazineEntries.${index}.discountPercentage`,
                                    )}
                                    onChange={(e) => {
                                      setValue(
                                        `magazineEntries.${index}.discountPercentage`,
                                        e.target.value,
                                      );
                                      setHasUserMadeChanges(true);
                                    }}
                                    disabled={entry.isLocked}
                                    className={`block w-full border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                      entry.isLocked
                                        ? "bg-gray-100 cursor-not-allowed"
                                        : "border-gray-300"
                                    }`}
                                  />
                                </td>

                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{
                                    minWidth: "90px",
                                    width: "100px",
                                  }}
                                >
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register(
                                      `magazineEntries.${index}.discountValue`,
                                    )}
                                    onChange={(e) => {
                                      setValue(
                                        `magazineEntries.${index}.discountValue`,
                                        e.target.value,
                                      );
                                      setHasUserMadeChanges(true);
                                    }}
                                    disabled={entry.isLocked}
                                    className={`block w-full border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                      entry.isLocked
                                        ? "bg-gray-100 cursor-not-allowed"
                                        : "border-gray-300"
                                    }`}
                                  />
                                </td>

                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{
                                    minWidth: "90px",
                                    width: "100px",
                                  }}
                                >
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register(
                                      `magazineEntries.${index}.additionalCharge`,
                                    )}
                                    onChange={(e) => {
                                      setValue(
                                        `magazineEntries.${index}.additionalCharge`,
                                        e.target.value,
                                      );
                                      setHasUserMadeChanges(true);
                                    }}
                                    disabled={entry.isLocked}
                                    className={`block w-full border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                      entry.isLocked
                                        ? "bg-gray-100 cursor-not-allowed"
                                        : "border-gray-300"
                                    }`}
                                  />
                                </td>

                                <td
                                  className="px-3 py-4 text-sm font-medium whitespace-nowrap"
                                  style={{
                                    minWidth: "90px",
                                    width: "100px",
                                  }}
                                >
                                  {" "}
                                  {formatCurrency(
                                    calculateRowTotal(entry),
                                  )}{" "}
                                </td>

                                <td
                                  className="px-3 py-4 whitespace-nowrap"
                                  style={{ width: "200px" }}
                                >
                                  <div className="flex items-center space-x-2">
                                    <MonthlyArtworkSelector
                                      monthlyArtwork={
                                        entry.monthlyArtwork || []
                                      }
                                      onMonthlyArtworkChange={(
                                        monthlyArtwork,
                                        isOngoing,
                                      ) =>
                                        handleMonthlyArtworkChange(
                                          index,
                                          monthlyArtwork,
                                          isOngoing,
                                        )
                                      }
                                      disabled={
                                        !entry.magazine ||
                                        !entry.monthlyArtwork ||
                                        entry.monthlyArtwork.length === 0
                                      }
                                      isOngoing={entry.isOngoing || false}
                                      hasPublication={!!entry.magazine}
                                    />{" "}
                                    {entry.monthlyArtwork &&
                                      entry.monthlyArtwork.filter(
                                        (m) => m.isSelected,
                                      ).length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            toggleRowExpansion(index)
                                          }
                                          className="px-2 py-1 text-xs text-blue-600 rounded border border-blue-300 hover:text-blue-800"
                                          title={
                                            expandedRows.has(index)
                                              ? "Hide Details"
                                              : "Show Details"
                                          }
                                        >
                                          {" "}
                                          {expandedRows.has(index)
                                            ? "Hide"
                                            : "Details"}{" "}
                                        </button>
                                      )}{" "}
                                  </div>
                                  {/* Hidden inputs for form validation */}
                                  <input
                                    type="hidden"
                                    {...register(
                                      `magazineEntries.${index}.monthlyArtwork`,
                                    )}
                                  />
                                  {/* Ongoing is now toggled inside the modal; keep the
                                      field registered so it stays in the submitted form. */}
                                  <input
                                    type="hidden"
                                    {...register(
                                      `magazineEntries.${index}.isOngoing`,
                                    )}
                                  />
                                </td>

                                <td
                                  className="px-2 py-4 text-sm font-medium whitespace-nowrap"
                                  style={{ width: "50px" }}
                                >
                                  <div className="flex space-x-1">
                                    <button
                                      type="button"
                                      onClick={() => copyRow(index)}
                                      disabled={hasSelectedClosedIssues(entry)}
                                      className={`${
                                        hasSelectedClosedIssues(entry)
                                          ? "text-gray-400 cursor-not-allowed"
                                          : "text-blue-600 hover:text-blue-900"
                                      }`}
                                      title={
                                        hasSelectedClosedIssues(entry)
                                          ? "Cannot copy - booking has closed issues selected"
                                          : "Copy Row"
                                      }
                                    >
                                      <Copy className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => deleteRow(index)}
                                      className={
                                        "text-red-600 hover:text-red-900"
                                      }
                                      title="Delete Row"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {/* Expanded Artwork Details Row */}
                              {expandedRows.has(index) &&
                                entry.monthlyArtwork &&
                                entry.monthlyArtwork.filter((m) => m.isSelected)
                                  .length > 0 && (
                                  <tr>
                                    <td
                                      colSpan="9"
                                      className="px-4 py-2 bg-gray-50"
                                    >
                                      <div className="p-4 rounded-lg border border-gray-200">
                                        <div
                                          className="grid gap-4 mb-2 text-xs font-medium text-gray-500"
                                          style={{
                                            gridTemplateColumns:
                                              "80px 1fr 1fr 40px",
                                          }}
                                        >
                                          <div>Issue</div>
                                          <div>Note</div>
                                          <div>Filename</div>
                                          <div></div>
                                        </div>
                                        {entry.monthlyArtwork
                                          .filter(
                                            (artwork) => artwork.isSelected,
                                          )
                                          .map((artwork, artworkIndex) => (
                                            <div
                                              key={artworkIndex}
                                              className="grid gap-4 py-1 text-sm border-b border-gray-100 last:border-b-0"
                                              style={{
                                                gridTemplateColumns:
                                                  "80px 1fr 1fr 40px",
                                              }}
                                            >
                                              <div className="text-gray-900">
                                                {" "}
                                                {artwork.month}
                                              </div>
                                              <div className="text-gray-600">
                                                {" "}
                                                {artwork.artworkNote || ""}{" "}
                                              </div>
                                              <div className="text-gray-600">
                                                {" "}
                                                {artwork.filename || ""}{" "}
                                              </div>
                                              <div></div>
                                            </div>
                                          ))}{" "}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Toggle to reveal closed bookings (hidden by default) */}
                  {closedBookingCount > 0 && (
                    <div className="flex justify-end mt-4">
                      <label className="flex items-center text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showClosedBookings}
                          onChange={(e) =>
                            setShowClosedBookings(e.target.checked)
                          }
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="ml-2">
                          Show {closedBookingCount} closed booking
                          {closedBookingCount === 1 ? "" : "s"}
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}
            {selectedCustomer && (
              <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                <div className="flex justify-end">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      Total value of this booking:
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {" "}
                      {formatCurrency(calculateTotalValue())}{" "}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700">
                    Additional Notes
                  </label>
                  <textarea
                    {...register("notes")}
                    rows={3}
                    className="block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Any additional notes for this booking..."
                  />
                </div>

                {/* Unsaved Changes Indicator */}
                {hasUnsavedArtworkChanges && (
                  <div className="p-4 mt-4 bg-amber-50 rounded-md border border-amber-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-amber-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-amber-800">
                          Unsaved Artwork Changes
                        </h3>
                        <div className="mt-1 text-sm text-amber-700">
                          You have made changes to artwork details. Click "{" "}
                          {isEditMode ? "Update Booking" : "Save Booking"}" to
                          save all changes.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    type="submit"
                    className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      hasUnsavedArtworkChanges
                        ? "bg-amber-600 animate-pulse hover:bg-amber-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    <Save className="mr-2 w-5 h-5" />{" "}
                    {isEditMode ? "Update Booking" : "Save Booking"}
                    {hasUnsavedArtworkChanges && (
                      <span className="inline-flex items-center px-2 py-0.5 ml-2 text-xs font-medium text-amber-800 bg-white rounded-full">
                        !
                      </span>
                    )}{" "}
                  </button>
                </div>
              </div>
            )}{" "}
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default NewBooking;
